/**
 * The documents attached to the chat — WHICH ones, never what they are (MAR-125). Composed by the
 * `web-chat` store facade (MAR-948); the documents store owns the rows, this owns the id list and
 * its durable twin, `conversation_documents` (AlexAmin/marcus#83).
 *
 * Two halves, deliberately:
 *
 * - **`contextDocumentIds`** rides each `/api/chat` request and becomes `DocumentScope.attachedIds`
 *   (MAR-114). It is the source of truth for the CURRENT turn.
 * - **`conversation_documents`** is what makes a reloaded thread remember its chips. Writes to it are
 *   FIRE-AND-FORGET: a failure logs rather than blocking the composer, and both endpoints are
 *   idempotent, so a rapid attach/detach pair converges whatever order the requests land in.
 */
import { computed, ref, type Ref } from 'vue'
import type { ApiFetch } from '~/composables/useApi'
import type { ConversationDocument } from '~~/shared/web-chat/conversationDocument'
import { documentRowStub, type ItemRow } from '~~/shared/items/itemRow'
import { isItemPending } from '~~/shared/items/itemPending'
import { setWith, setWithout } from '~/stores/entityCache'
import { isUuid } from '#shared/uuid'

export interface ChatAttachmentsDeps {
  conversationId: Ref<string | null>
  apiFetch: ApiFetch
  /** The documents store's row cache — read to resolve a chip, seeded when a caller brings a row. */
  documents: () => { get: (id: string) => ItemRow | null | undefined; upsert: (rows: ItemRow[]) => void }
}

export type AttachableDocument = {
  id: string; type?: string; name?: string; file_name?: string | null; status?: string | null; duplicate?: boolean
}

export function useChatAttachments({ conversationId, apiFetch, documents }: ChatAttachmentsDeps) {
  const contextDocumentIds = ref<string[]>([])
  /**
   * Attachments whose upload was REFUSED because the org already held those bytes, so an existing
   * item was attached instead (Documents doc §5). Display-only, and a property of this attachment
   * event rather than of the document — which is why it is not a field on the row.
   */
  const duplicateAttachments = ref<Set<string>>(new Set())

  /** The attached documents, resolved through the documents store, in attachment order. */
  const contextDocuments = computed(() => {
    const store = documents()
    return contextDocumentIds.value.map((id) => {
      const row = store.get(id)
      return {
        id,
        name: row?.name ?? '',
        file_name: row?.file_name ?? null,
        status: row?.status ?? null,
        duplicate: duplicateAttachments.value.has(id) || undefined,
      }
    })
  })

  /** True while any attachment is still uploading or indexing — gates the send button. */
  const attachmentsProcessing = computed(() => contextDocuments.value.some(d => isItemPending(d.status)))
  const attachmentsProcessingCount = computed(() => contextDocuments.value.filter(d => isItemPending(d.status)).length)

  function isAttached(id: string): boolean {
    return contextDocumentIds.value.includes(id)
  }

  /**
   * ONLY A UUID IS PERSISTED, and that guard is load-bearing. The chat upload path attaches
   * OPTIMISTIC chips whose ids are client-minted (`pending-1757…`); they never become rows and are
   * detached the moment the upload returns. Posting one would be a guaranteed 404 per dropped file.
   * No conversation row yet → the link waits for `flushAttachments`, called by `createConversation`.
   */
  function persistAttachment(itemId: string) {
    const id = conversationId.value
    if (!id || !isUuid(itemId)) return
    apiFetch(`/api/conversations/${id}/documents`, { method: 'POST', body: { itemId } })
      .catch(err => console.warn('[webChatStore] could not remember attachment:', err))
  }

  function unpersistAttachment(itemId: string) {
    const id = conversationId.value
    if (!id || !isUuid(itemId)) return
    apiFetch(`/api/conversations/${id}/documents/${itemId}`, { method: 'DELETE' })
      .catch(err => console.warn('[webChatStore] could not forget attachment:', err))
  }

  /**
   * Attach a document. Folders are ignored; re-attaching a known id is a no-op. Wired here rather
   * than at each of the attach sites (drag-drop, the picker, a search result, a confirm card, the
   * document page, chat upload…) so a new attach path cannot forget to persist.
   *
   * The row is seeded into the documents store when the caller brought facts the cache lacks — agent
   * search hits and confirm cards attach documents no listing has fetched. `upsert` merges, so this
   * can never blank a richer row that is already cached.
   */
  function attachDocument(item: AttachableDocument, options: { persist?: boolean } = {}) {
    if (item.type === 'folder') return
    const store = documents()
    if (item.name && !store.get(item.id)) {
      store.upsert([documentRowStub({
        id: item.id,
        name: item.name,
        file_name: item.file_name ?? null,
        status: item.status ?? null,
      })])
    }
    if (item.duplicate) duplicateAttachments.value = setWith(duplicateAttachments.value, item.id)
    if (contextDocumentIds.value.includes(item.id)) return
    contextDocumentIds.value = [...contextDocumentIds.value, item.id]
    if (options.persist !== false) persistAttachment(item.id)
  }

  function detachDocument(id: string) {
    contextDocumentIds.value = contextDocumentIds.value.filter(d => d !== id)
    unpersistAttachment(id)
    if (duplicateAttachments.value.has(id)) duplicateAttachments.value = setWithout(duplicateAttachments.value, id)
  }

  /**
   * Local-only: resets the refs and writes nothing, so the conversation being left keeps the rows it
   * remembers. Callers run it BEFORE `conversationId` changes hands (see `switchTo`/`startNewChat`).
   */
  function clearAttachments() {
    contextDocumentIds.value = []
    duplicateAttachments.value = new Set()
  }

  /**
   * Write what the composer accumulated BEFORE the conversation row existed (#83). Dropping a file on
   * the welcome screen and then typing is the ordinary way such a thread starts. Called by
   * `createConversation` AFTER it sets `conversationId`, or `persistAttachment` would return early.
   */
  function flushAttachments() {
    for (const itemId of contextDocumentIds.value) persistAttachment(itemId)
  }

  /**
   * Put back the documents this thread remembers, on load. `persist: false`: these rows ARE the
   * durable list, and writing each back would be one redundant POST per attachment per open.
   */
  async function restoreConversationDocuments(id: string) {
    const stored = await apiFetch<ConversationDocument[]>(`/api/conversations/${id}/documents`)
    // The user may have switched conversations while this was in flight; applying it then would
    // paste one thread's attachments onto another.
    if (conversationId.value !== id) return
    for (const row of stored) {
      attachDocument({ id: row.id, name: row.name, file_name: row.file_name, status: row.status }, { persist: false })
    }
  }

  return {
    contextDocumentIds,
    contextDocuments,
    attachmentsProcessing,
    attachmentsProcessingCount,
    isAttached,
    attachDocument,
    detachDocument,
    clearAttachments,
    flushAttachments,
    restoreConversationDocuments,
  }
}
